import React, { useState, useEffect, useCallback } from 'react';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  Wallet,
  CreditCard,
  FileText,
  XCircle,
  Building2,
  Briefcase,
  Hash,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { agentAPI } from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: RefreshCw }
};

const TYPE_CONFIG = {
  APPLICATION: { label: 'University', icon: Building2, color: 'bg-purple-100 text-purple-700' },
  VAS: { label: 'Service', icon: Briefcase, color: 'bg-teal-100 text-teal-700' }
};

export default function AgentCommission() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    pendingAmount: 0,
    approvedAmount: 0,
    paidAmount: 0,
    thisMonth: 0,
    growth: 0,
    totalCount: 0
  });
  const [wallet, setWallet] = useState({
    availableBalance: 0,
    pendingBalance: 0,
    lifetimeEarnings: 0,
    payoutThreshold: 100,
    isPayoutEligible: false
  });
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [commissionBreakdown, setCommissionBreakdown] = useState([]);
  const [universityBreakdown, setUniversityBreakdown] = useState([]);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Build filter params for commission history
      const historyParams = { page, limit: 20 };
      if (statusFilter !== 'all') historyParams.status = statusFilter;
      if (typeFilter !== 'all') historyParams.commissionType = typeFilter;
      if (dateFilter !== 'all') {
        const now = new Date();
        if (dateFilter === 'thisMonth') {
          historyParams.startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
          historyParams.endDate = now.toISOString();
        } else if (dateFilter === 'lastMonth') {
          historyParams.startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
          historyParams.endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString();
        } else if (dateFilter === 'last3Months') {
          historyParams.startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString();
          historyParams.endDate = now.toISOString();
        }
      }

      const [statsRes, historyRes] = await Promise.all([
        agentAPI.getCommissionStats(),
        agentAPI.getCommissionHistory(historyParams)
      ]);

      setStats(statsRes.stats || {});
      setWallet(statsRes.wallet || {});
      setTransactions(historyRes.transactions || []);
      setCommissionBreakdown(historyRes.breakdown || []);
      setUniversityBreakdown(historyRes.universityBreakdown || []);
      setPagination(historyRes.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (error) {
      console.error('Failed to fetch commission data:', error);
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, dateFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const response = await agentAPI.exportCommissions({
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commissions-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Commission report downloaded');
    } catch (error) {
      toast.error('Failed to export commissions');
    }
  };

  const handleWithdraw = async () => {
    try {
      setWithdrawLoading(true);
      const amount = parseFloat(withdrawAmount) || wallet.availableBalance;
      await agentAPI.requestPayout(amount, withdrawNote);
      toast.success('Withdrawal request submitted. Processing may take 2-3 business days.');
      setWithdrawDialog(false);
      setWithdrawAmount('');
      setWithdrawNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit withdrawal request');
    } finally {
      setWithdrawLoading(false);
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-commission-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Commission & Earnings</h1>
            <p className="text-gray-600">Track your commission and payment history</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button
              onClick={() => {
                setWithdrawAmount(String(wallet.availableBalance));
                setWithdrawDialog(true);
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
              disabled={!wallet.isPayoutEligible}
            >
              <Wallet className="w-4 h-4 mr-2" />
              Request Withdrawal
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="p-5 bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-100 mb-1">Total Earnings</p>
                  <p className="text-3xl font-bold">${(stats.totalEarnings || 0).toLocaleString()}</p>
                  <div className="flex items-center gap-1 mt-2 text-sm text-emerald-100">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>{stats.growth >= 0 ? '+' : ''}{stats.growth || 0}% this month</span>
                  </div>
                </div>
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-8 h-8" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="p-5 bg-white border-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Available Balance</p>
                  <p className="text-3xl font-bold text-blue-600">${(wallet.availableBalance || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-2">Ready to withdraw</p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="p-5 bg-white border-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Pending</p>
                  <p className="text-3xl font-bold text-amber-600">${(stats.pendingAmount || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-2">Awaiting approval</p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="p-5 bg-white border-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">This Month</p>
                  <p className="text-3xl font-bold text-green-600">${(stats.thisMonth || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-2">{stats.totalCount || 0} total commissions</p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Transactions</TabsTrigger>
            <TabsTrigger value="breakdown" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Breakdown</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Transactions */}
              <Card className="p-6 bg-white border-0 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-outfit font-bold text-gray-900">Recent Transactions</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('transactions')}>
                    View All
                  </Button>
                </div>

                {transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No commissions yet</p>
                    <p className="text-sm text-gray-400 mt-1">Commissions are created when applications or services are completed</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.slice(0, 4).map((transaction, index) => {
                      const statusConfig = STATUS_CONFIG[transaction.status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      const typeConfig = TYPE_CONFIG[transaction.commissionType] || TYPE_CONFIG.VAS;

                      return (
                        <motion.div
                          key={transaction.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {transaction.student?.firstName?.[0]}{transaction.student?.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {transaction.student?.firstName} {transaction.student?.lastName}
                              </p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-gray-500">{transaction.service}</p>
                                <Badge className={`${typeConfig.color} text-[10px] px-1.5 py-0`}>
                                  {typeConfig.label}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">${transaction.amount}</p>
                            <Badge className={`${statusConfig.color} text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </Card>

              {/* Commission Breakdown */}
              <Card className="p-6 bg-white border-0 shadow-sm">
                <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Commission by Service</h3>

                {commissionBreakdown.length === 0 ? (
                  <div className="text-center py-8">
                    <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No breakdown data available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {commissionBreakdown.map((item, index) => (
                      <motion.div
                        key={item.service}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium text-gray-700">{item.service}</span>
                          <span className="text-sm text-gray-600">${item.total.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${item.percentage}%` }}
                            transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{item.count} referrals</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="transactions" className="mt-4">
            <Card className="p-6 bg-white border-0 shadow-sm">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="APPLICATION">University</SelectItem>
                    <SelectItem value="VAS">Service (VAS)</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={dateFilter} onValueChange={(v) => { setDateFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-48">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="thisMonth">This Month</SelectItem>
                    <SelectItem value="lastMonth">Last Month</SelectItem>
                    <SelectItem value="last3Months">Last 3 Months</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {/* Transactions Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Reference</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Student</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Details</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => {
                      const statusConfig = STATUS_CONFIG[transaction.status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      const typeConfig = TYPE_CONFIG[transaction.commissionType] || TYPE_CONFIG.VAS;

                      return (
                        <tr key={transaction.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              <Hash className="w-3 h-3 text-gray-400" />
                              <span className="text-xs font-mono text-gray-600">{transaction.referenceId || '-'}</span>
                            </div>
                            {transaction.invoiceNumber && (
                              <p className="text-[10px] text-gray-400 mt-0.5">{transaction.invoiceNumber}</p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                                {transaction.student?.firstName?.[0]}{transaction.student?.lastName?.[0]}
                              </div>
                              <span className="font-medium text-gray-900 text-sm">
                                {transaction.student?.firstName} {transaction.student?.lastName}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`${typeConfig.color} text-xs`}>
                              {typeConfig.label}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-700">
                              {transaction.universityName || transaction.serviceType?.replace(/_/g, ' ') || transaction.service}
                            </p>
                            {transaction.programName && (
                              <p className="text-xs text-gray-400">{transaction.programName}</p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-semibold text-gray-900">${transaction.amount}</p>
                            <p className="text-[10px] text-gray-400">{transaction.percentage}% of ${transaction.baseAmount}</p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-sm">
                            {transaction.paidAt
                              ? format(new Date(transaction.paidAt), 'MMM d, yyyy')
                              : format(new Date(transaction.createdAt), 'MMM d, yyyy')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {transactions.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No transactions found</p>
                  <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                </div>
              )}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Showing {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pagination.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="breakdown" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Service Breakdown */}
              <Card className="p-6 bg-white border-0 shadow-sm">
                <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Earnings by Service</h3>

                {commissionBreakdown.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No service data available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {commissionBreakdown.map((item) => (
                      <div key={item.service} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{item.service}</span>
                          <span className="text-lg font-bold text-emerald-600">${item.total.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{item.count} referrals</span>
                          <span>{item.percentage}% of total</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <div className="space-y-6">
                {/* University Breakdown */}
                {universityBreakdown.length > 0 && (
                  <Card className="p-6 bg-white border-0 shadow-sm">
                    <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Earnings by University</h3>
                    <div className="space-y-3">
                      {universityBreakdown.map((item) => (
                        <div key={item.university} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{item.university}</p>
                              <p className="text-xs text-gray-500">{item.count} applications</p>
                            </div>
                          </div>
                          <p className="font-semibold text-gray-900">${item.total.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Payment Info */}
                <Card className="p-6 bg-white border-0 shadow-sm">
                  <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Payment Information</h3>

                  <div className="space-y-4">
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                      <div className="flex items-center gap-3 mb-2">
                        <CreditCard className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium text-emerald-900">Bank Transfer</span>
                      </div>
                      <p className="text-sm text-emerald-700">Primary payment method</p>
                      <p className="text-xs text-emerald-600 mt-2">Processing time: 2-3 business days</p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">Payment Schedule</h4>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Commissions calculated after service completion
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Admin approval required before withdrawal
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Minimum withdrawal: ${wallet.payoutThreshold}
                        </li>
                      </ul>
                    </div>

                    <Button
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                      onClick={() => {
                        setWithdrawAmount(String(wallet.availableBalance));
                        setWithdrawDialog(true);
                      }}
                      disabled={!wallet.isPayoutEligible}
                    >
                      <Wallet className="w-4 h-4 mr-2" />
                      Request Withdrawal (${wallet.availableBalance.toLocaleString()})
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Withdrawal Dialog */}
        <Dialog open={withdrawDialog} onOpenChange={setWithdrawDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Withdrawal</DialogTitle>
              <DialogDescription>
                Enter the amount you'd like to withdraw from your available balance of ${wallet.availableBalance.toLocaleString()}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Amount ($)</label>
                <Input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder={`Max: ${wallet.availableBalance}`}
                  min={wallet.payoutThreshold}
                  max={wallet.availableBalance}
                />
                <p className="text-xs text-gray-400 mt-1">Minimum: ${wallet.payoutThreshold}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Note (optional)</label>
                <Input
                  value={withdrawNote}
                  onChange={(e) => setWithdrawNote(e.target.value)}
                  placeholder="Any special instructions..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWithdrawDialog(false)}>Cancel</Button>
              <Button
                onClick={handleWithdraw}
                disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) < wallet.payoutThreshold}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {withdrawLoading ? 'Processing...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AgentLayout>
  );
}
